<script setup>
import { computed } from 'vue';
import { buildThreadTree, getEntryNodes } from '../mobileFlowBuilder';
import FlowThreadNode from './FlowThreadNode.vue';

const props = defineProps({
  definition: {
    type: Object,
    required: true,
  },
  selectedNodeId: {
    type: String,
    default: '',
  },
  quickInsertItems: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'select',
  'insertAfter',
  'updateNode',
  'deleteNode',
  'duplicateNode',
  'addInteractiveButton',
  'moveInteractiveButton',
  'addInteractiveListRow',
  'addInteractiveListSection',
  'quickInsert',
]);
const uiCopy = {
  time: '9:41',
  businessName: 'Autorply',
  businessStatus: 'نشط الآن',
};
const threadTree = computed(() => buildThreadTree(props.definition));
const hasEntry = computed(() => getEntryNodes(props.definition).length > 0);

const quickActions = computed(() => props.quickInsertItems.slice(0, 4));

const quickActionLabel = action => action.shortLabel || action.label;

const clearSelection = event => {
  if (event.target === event.currentTarget) {
    emit('select', '');
  }
};
</script>

<template>
  <div
    class="flex min-h-screen w-full min-w-[30rem] items-center justify-center bg-transparent p-4"
  >
    <div
      class="relative mx-auto flex h-full w-full max-w-[28rem] min-w-[26rem] items-center justify-center"
    >
      <div class="mx-auto w-full max-w-[24.25rem] min-w-[24.25rem] shrink-0">
        <div
          class="relative overflow-hidden rounded-[3.6rem] bg-[#0a0a0a] p-[0.22rem] shadow-[0_40px_100px_rgba(15,23,42,0.4)] ring-1 ring-black/20 dark:shadow-[0_44px_110px_rgba(0,0,0,0.65)]"
        >
          <span
            class="pointer-events-none absolute left-[-2px] top-[11rem] z-20 h-14 w-[3px] rounded-r-full bg-white/18"
          />
          <span
            class="pointer-events-none absolute left-[-2px] top-[15rem] z-20 h-20 w-[3px] rounded-r-full bg-white/18"
          />
          <span
            class="pointer-events-none absolute right-[-2px] top-[13rem] z-20 h-24 w-[3px] rounded-l-full bg-white/18"
          />
          <div
            class="relative aspect-[430/932] overflow-hidden rounded-[3.35rem] bg-[#e9dfcf]"
          >
            <div
              class="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.35),transparent_30%),linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0)),radial-gradient(circle_at_1px_1px,rgba(160,132,96,0.08)_1px,transparent_0)] [background-size:auto,auto,28px_28px]"
            />

            <div
              class="pointer-events-none absolute left-1/2 top-3 z-20 h-8 w-36 -translate-x-1/2 rounded-full bg-black shadow-[inset_0_-1px_0_rgba(255,255,255,0.06)]"
            />

            <div class="relative z-10 flex h-full flex-col">
              <div
                class="border-b border-black/10 bg-[#fafaf8] px-4 pb-3 pt-8 shadow-[0_2px_8px_rgba(15,23,42,0.08)]"
              >
                <div
                  class="mb-2 flex items-center justify-between text-[11px] font-semibold text-black"
                >
                  <span>{{ uiCopy.time }}</span>
                  <div class="flex items-center gap-1.5">
                    <span class="flex items-end gap-[2px]">
                      <span class="h-1.5 w-[2px] rounded-full bg-black" />
                      <span class="h-2 w-[2px] rounded-full bg-black" />
                      <span class="h-2.5 w-[2px] rounded-full bg-black" />
                      <span class="h-3 w-[2px] rounded-full bg-black" />
                    </span>
                    <i class="i-lucide-wifi text-[12px] text-black" />
                    <i class="i-lucide-battery-full text-[15px] text-black" />
                  </div>
                </div>

                <div class="flex items-center justify-between gap-3">
                  <div class="flex items-center gap-0.5">
                    <button
                      type="button"
                      class="flex h-8 w-8 items-center justify-center rounded-full text-black/60 transition hover:bg-black/5"
                    >
                      <i class="i-lucide-ellipsis text-[18px]" />
                    </button>
                    <button
                      type="button"
                      class="flex h-8 w-8 items-center justify-center rounded-full text-black/60 transition hover:bg-black/5"
                    >
                      <i class="i-lucide-phone text-[15px]" />
                    </button>
                    <button
                      type="button"
                      class="flex h-8 w-8 items-center justify-center rounded-full text-black/60 transition hover:bg-black/5"
                    >
                      <i class="i-lucide-video text-[17px]" />
                    </button>
                  </div>

                  <div class="flex min-w-0 items-center gap-2.5">
                    <div class="min-w-0 text-right">
                      <div
                        class="flex items-center justify-end gap-1 truncate text-[14px] font-semibold text-black"
                      >
                        <span class="truncate">{{ uiCopy.businessName }}</span>
                        <i
                          class="i-lucide-badge-check text-[14px] text-[#25d366]"
                        />
                      </div>
                      <div class="text-[11px] text-black/60">
                        {{ uiCopy.businessStatus }}
                      </div>
                    </div>

                    <div
                      class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#25d366]/15 text-[#128c7e]"
                    >
                      <i class="i-woot-whatsapp text-lg" />
                    </div>
                  </div>
                </div>

                <div class="mt-3 flex flex-wrap gap-2">
                  <button
                    v-for="action in quickActions"
                    :key="action.id"
                    type="button"
                    class="rounded-full bg-white px-3 py-1 text-[11px] font-medium text-black/70 shadow-sm ring-1 ring-black/8 transition hover:bg-[#25d366]/8 hover:text-[#0f6b5a] hover:ring-[#25d366]/15"
                    @click="emit('quickInsert', action.id)"
                  >
                    {{ quickActionLabel(action) }}
                  </button>
                </div>
              </div>

              <div
                class="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-3 py-3 pb-14 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                @click="clearSelection"
              >
                <div
                  v-if="!hasEntry"
                  class="mx-auto mt-16 max-w-[14rem] rounded-[1.5rem] border border-dashed border-[#25d366]/25 bg-white/80 p-5 text-center shadow-sm"
                >
                  <p class="text-[13px] font-semibold text-black/80">
                    {{ $t('MOBILE_CONVERSATION_FLOWS.EDITOR.EMPTY_TITLE') }}
                  </p>
                  <p class="mt-2 text-[12px] leading-5 text-black/60">
                    {{
                      $t('MOBILE_CONVERSATION_FLOWS.EDITOR.EMPTY_DESCRIPTION')
                    }}
                  </p>
                </div>

                <FlowThreadNode
                  v-else-if="threadTree"
                  :item="threadTree"
                  :quick-insert-items="quickInsertItems"
                  :selected-node-id="selectedNodeId"
                  @select="emit('select', $event)"
                  @insert-after="emit('insertAfter', $event)"
                  @update-node="emit('updateNode', $event)"
                  @delete-node="emit('deleteNode', $event)"
                  @duplicate-node="emit('duplicateNode', $event)"
                  @add-interactive-button="emit('addInteractiveButton', $event)"
                  @move-interactive-button="
                    emit('moveInteractiveButton', $event)
                  "
                  @add-interactive-list-row="
                    emit('addInteractiveListRow', $event)
                  "
                  @add-interactive-list-section="
                    emit('addInteractiveListSection', $event)
                  "
                  @quick-insert="emit('quickInsert', $event)"
                />
              </div>

              <div
                class="pointer-events-none absolute bottom-2 left-1/2 z-20 h-1.5 w-28 -translate-x-1/2 rounded-full bg-black/80"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
